import { routerService } from "cruzo";
import { clearAllLessonProgressFromStorage } from "site/services/lesson-progress-storage";
import { clearLocalLastUpdateStatsMs } from "site/services/user-stats-storage";
import { clearLocalLastUpdateSettingsMs } from "site/services/user-settings-storage";
import { appService } from "site/services/app.service";
import { routerUrlBucket } from "site/urls";
import { scheduleUserDataSync } from "site/services/user-sync.service";
import { uiLang } from "site/services/i18n";
import { subscriptionApiBaseUrl } from "site/services/xq-api-base-url";
import {
  ENVELOPE_PURPOSE_REFRESH,
  ENVELOPE_PURPOSE_VERIFY,
  openSessionEnvelope,
  verifyEnvelopeSecret,
} from "site/services/session-envelope";
import { toBufferSource } from "site/utils";

export type {
  AuthSessionBookStatus,
  AuthSessionPayload,
  AuthSessionSubscription,
  AuthSubscriptionStatus,
} from "site/services/auth-session.types";

import type {
  AuthSessionBookStatus,
  AuthSessionPayload,
  AuthSubscriptionStatus,
} from "site/services/auth-session.types";

export const SESSION_TOKEN_STORAGE_KEY = "token";

const AUTH_SESSION_READY_WAIT_MS = 8_000;
const AUTH_REFRESH_FETCH_TIMEOUT_MS = 20_000;

const bookKeyBytesById = new Map<string, Uint8Array>();
const bookStatusById = new Map<string, AuthSessionBookStatus>();
let sessionEmail = "";
let sessionPermissions: string[] = [];
let unauthorizedLogoutInFlight: Promise<void> | null = null;
let refreshAuthSessionInFlight: Promise<AuthSessionPayload> | null = null;
let sessionBootstrapPromise: Promise<void> | null = null;

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      window.setTimeout(() => reject(new Error(`${label}: timeout`)), ms);
    }),
  ]);
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init?: RequestInit,
  timeoutMs = AUTH_REFRESH_FETCH_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) {
      throw new Error(`fetch timeout (${timeoutMs}ms)`);
    }
    throw e;
  } finally {
    window.clearTimeout(timer);
  }
}

function redirectAfterUnauthorizedLogout(): void {
  const path = (routerService.pathname$.actual || "/").replace(/\/+$/, "") || "/";
  const m = path.match(/^\/books\/([^/]+)(?:\/([^/]+))?(?:\/(edit))?\/?$/);
  if (m?.[3] === "edit" && m[1]) {
    routerService.pushHistory(routerUrlBucket.buildUrl("book_exercises", { bookId: m[1] }));
    return;
  }
  if (m?.[2]) {
    routerService.pushHistory(routerUrlBucket.buildUrl("books"));
  }
}

export function getSessionToken(): string | null {
  const t = localStorage.getItem(SESSION_TOKEN_STORAGE_KEY) ?? "";
  return t || null;
}

export function setSessionToken(token: string): void {
  localStorage.setItem(SESSION_TOKEN_STORAGE_KEY, token);
}

export async function logoutOnUnauthorized(): Promise<void> {
  if (!getSessionToken()) {
    return;
  }
  if (unauthorizedLogoutInFlight) {
    return unauthorizedLogoutInFlight;
  }
  unauthorizedLogoutInFlight = (async () => {
    await logoutAuthSession();
    redirectAfterUnauthorizedLogout();
  })().finally(() => {
    unauthorizedLogoutInFlight = null;
  });
  return unauthorizedLogoutInFlight;
}

function authFetchHeaders(init?: RequestInit): Headers {
  const headers = new Headers(init?.headers);
  const token = getSessionToken();
  if (token && !headers.has("Authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

export async function authFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  const response = await fetch(input, { ...init, headers: authFetchHeaders(init) });
  if (response.status === 401) {
    await logoutOnUnauthorized();
  }
  return response;
}

function normalizePermissions(raw: unknown): string[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map((p) => String(p).trim()).filter(Boolean);
}

async function clearSessionLocalState(): Promise<void> {
  localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
  bookKeyBytesById.clear();
  bookStatusById.clear();
  sessionEmail = "";
  sessionPermissions = [];
  sessionBootstrapPromise = null;
  appService.sessionPermissions$.update([]);
  clearAllLessonProgressFromStorage();
  clearLocalLastUpdateStatsMs();
  clearLocalLastUpdateSettingsMs();
  appService.resetLessonProgressOnLogout();
  appService.setSubscriptionAccessLoading(false);
  appService.notifySubscriptionAccessChanged();
}

export function clearSessionToken(): void {
  void clearSessionLocalState();
}

export function getSessionPermissions(): readonly string[] {
  return sessionPermissions;
}

export function hasEditorPermission(): boolean {
  return sessionPermissions.some((p) => String(p).trim().toLowerCase() === "editor");
}

export function hasOwnerPermission(): boolean {
  return sessionPermissions.some((p) => String(p).trim().toLowerCase() === "owner");
}

export function getSessionEmail(): string | null {
  const e = sessionEmail;
  return e || null;
}

export function isAuthSessionActive(): boolean {
  if (!getSessionToken()) {
    return false;
  }
  return !!sessionEmail || sessionPermissions.length > 0;
}

export function getBookContentKeyBytesFromSession(bookId: string): Uint8Array | null {
  const bid = `${bookId}`;
  if (!bid) {
    return null;
  }
  return bookKeyBytesById.get(bid) ?? null;
}

export function hasBookContentKeyInSession(bookId: string): boolean {
  return getBookContentKeyBytesFromSession(bookId) !== null;
}

export function getSubscriptionBookStatus(bookId: string): AuthSessionBookStatus | undefined {
  const bid = `${bookId}`;
  if (!bid) {
    return undefined;
  }
  return bookStatusById.get(bid);
}

export function getSubscriptionStatusByBookId(): Readonly<Record<number, AuthSubscriptionStatus>> {
  const out: Record<number, AuthSubscriptionStatus> = {};
  for (const [bid, row] of bookStatusById) {
    const id = Number(bid);
    if (Number.isFinite(id)) {
      out[id] = row.status;
    }
  }
  return out;
}

async function decryptBookContentKeyEnc(
  bookId: string,
  enc: { iv: Uint8Array; cipher: Uint8Array },
  dkBytes: Uint8Array,
): Promise<Uint8Array> {
  const key = await crypto.subtle.importKey("raw", toBufferSource(dkBytes), { name: "AES-GCM" }, false, ["decrypt"]);
  const aad = new TextEncoder().encode(`xq_book_key|${bookId}`);
  const plain = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: toBufferSource(enc.iv), additionalData: aad },
    key,
    toBufferSource(enc.cipher),
  );
  return new Uint8Array(plain);
}

async function readEncryptedSessionResponse(
  response: Response,
  purpose: string,
  secret: string,
): Promise<AuthSessionPayload> {
  const blob = await response.arrayBuffer();
  return openSessionEnvelope(blob, purpose, secret);
}

export async function applyAuthSessionPayload(
  payload: AuthSessionPayload,
  opts?: { fromVerify?: boolean },
): Promise<void> {
  if (!payload.token) {
    throw new Error("auth session: missing token");
  }
  setSessionToken(payload.token);
  sessionEmail = payload.email ?? "";
  sessionPermissions = normalizePermissions(payload.permissions);
  appService.sessionPermissions$.update([...sessionPermissions]);
  const dkBytes = payload.dk;
  if (dkBytes.byteLength !== 32) {
    throw new Error("auth session: invalid dk length");
  }
  bookKeyBytesById.clear();
  bookStatusById.clear();
  for (const sub of payload.subscriptions ?? []) {
    const bid = String(sub.book_id);
    const status = sub.status;
    if (status) {
      bookStatusById.set(bid, {
        book_id: sub.book_id,
        status,
        access_active_until: sub.access_active_until,
      });
    }
    const enc = sub.book_content_key_enc;
    if (enc?.iv && enc?.cipher) {
      const keyBytes = await decryptBookContentKeyEnc(bid, enc, dkBytes);
      bookKeyBytesById.set(bid, keyBytes);
    }
  }
  appService.notifySubscriptionAccessChanged();
  const serverLastUpdateStatsMs = Number(payload.last_update_stats_ms) || 0;
  const serverLastUpdateSettingsMs = Number(payload.last_update_settings_ms) || 0;
  void scheduleUserDataSync({
    sessionSource: opts?.fromVerify ? "authorize" : "refresh",
    serverLastUpdateStatsMs,
    serverLastUpdateSettingsMs,
  })
    .then(() => appService.notifyLessonProgressChanged())
    .catch(() => {});
}

function authHeadersForToken(token: string, opts?: { jsonBody?: boolean }): Record<string, string> {
  const h: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };
  if (opts?.jsonBody) {
    h["Content-Type"] = "application/json";
  }
  return h;
}

export async function postAuthSendCode(email: string): Promise<void> {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  const r = await fetch(`${base}/auth/send-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, lang: uiLang() }),
  });
  if (!r.ok) {
    throw new Error(`auth send-code: HTTP ${r.status}`);
  }
}

export async function postAuthVerifyCode(email: string, code: string): Promise<AuthSessionPayload> {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  const r = await fetchWithTimeout(`${base}/auth/verify-code`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, code }),
  });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`auth verify-code: HTTP ${r.status}${detail ? ` (${detail.trim()})` : ""}`);
  }
  const secret = verifyEnvelopeSecret(email, code);
  const payload = await readEncryptedSessionResponse(r, ENVELOPE_PURPOSE_VERIFY, secret);
  await applyAuthSessionPayload(payload, { fromVerify: true });
  return payload;
}

async function refreshAuthSessionDirect(): Promise<AuthSessionPayload> {
  const base = subscriptionApiBaseUrl();
  if (!base) {
    throw new Error("VITE_XQ_API_BASE_URL is not set");
  }
  const bearer = getSessionToken();
  if (!bearer) {
    throw new Error("no session token");
  }

  const r = await fetchWithTimeout(`${base.replace(/\/+$/g, "")}/auth/session/refresh`, {
    method: "POST",
    headers: authHeadersForToken(bearer),
  });
  if (r.status === 401) {
    const err = new Error("auth session refresh: HTTP 401") as Error & { unauthorized?: boolean };
    err.unauthorized = true;
    throw err;
  }
  if (!r.ok) {
    throw new Error(`auth session refresh: HTTP ${r.status}`);
  }
  const payload = await readEncryptedSessionResponse(r, ENVELOPE_PURPOSE_REFRESH, bearer);
  await applyAuthSessionPayload(payload);
  return payload;
}

async function refreshAuthSessionWithLoading(): Promise<AuthSessionPayload> {
  appService.setSubscriptionAccessLoading(true);
  try {
    return await refreshAuthSessionDirect();
  } catch (e) {
    const unauthorized = !!(e && typeof e === "object" && (e as { unauthorized?: boolean }).unauthorized);
    if (unauthorized) {
      await clearSessionLocalState();
    }
    throw e;
  } finally {
    appService.setSubscriptionAccessLoading(false);
  }
}

export function kickoffAuthSessionBootstrap(): void {
  if (sessionBootstrapPromise) {
    return;
  }
  if (!subscriptionApiBaseUrl() || !getSessionToken()) {
    sessionBootstrapPromise = Promise.resolve();
    return;
  }
  sessionBootstrapPromise = refreshAuthSession()
    .then(() => {})
    .catch(() => {});
}

export async function ensureAuthSessionReady(): Promise<void> {
  if (!subscriptionApiBaseUrl() || !getSessionToken()) {
    return;
  }
  kickoffAuthSessionBootstrap();
  const wait = refreshAuthSessionInFlight ?? sessionBootstrapPromise;
  if (!wait) {
    return;
  }
  try {
    await withTimeout(Promise.resolve(wait).then((): void => undefined), AUTH_SESSION_READY_WAIT_MS, "ensureAuthSessionReady");
  } catch {
  }
}

export async function refreshAuthSession(): Promise<AuthSessionPayload> {
  if (refreshAuthSessionInFlight) {
    return refreshAuthSessionInFlight;
  }
  refreshAuthSessionInFlight = refreshAuthSessionWithLoading().finally(() => {
    refreshAuthSessionInFlight = null;
  });
  return refreshAuthSessionInFlight;
}

export async function logoutAuthSession(): Promise<void> {
  const base = subscriptionApiBaseUrl();
  const token = getSessionToken();
  if (base && token) {
    try {
      const r = await fetch(`${base}/auth/session/logout`, {
        method: "POST",
        headers: authHeadersForToken(token, { jsonBody: false }),
      });
      if (!r.ok && r.status !== 401) {
        throw new Error(`auth session logout: HTTP ${r.status}`);
      }
    } catch (e) {
      console.log(e);
    }
  }
  await clearSessionLocalState();
}
